import { useEffect, useRef, useState } from "react";

import BeakRightSmallIcon from "../../assets/img/icons/beak_right_small.svg";
import { REGIONS } from "../../config/config";
import { useSiteData } from "../../contexts/SiteDataContext";
import { useLocale } from "../../i18n/LocaleContext";
import type { Region } from "../../types";

function RegionSwitch() {
  const { siteData, setSiteData } = useSiteData();
  const { t } = useLocale();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLSpanElement>(null);
  const [modalLeftPosition, setModalLeftPosition] = useState(0);

  // Get regions excluding the currently selected one
  const otherRegions = REGIONS.filter(
    (region) => region.code !== siteData?.region?.code,
  );

  // Update modal position when opened or region changes
  useEffect(() => {
    /* istanbul ignore if */
    if (!triggerRef.current || !dropdownRef.current) return;
    const triggerRect = triggerRef.current.getBoundingClientRect();
    const containerRect = dropdownRef.current.getBoundingClientRect();
    const leftOffset = triggerRect.left - containerRect.left;
    setModalLeftPosition(leftOffset + triggerRect.width);
  }, [isOpen, siteData?.region]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        event.target instanceof Node &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleRegionSelect = (region: Region) => {
    setSiteData((prev) => ({
      ...prev,
      region: region,
    }));
    setIsOpen(false);
  };

  return (
    <div
      className="font-medium text-2xl leading-[120%] mb-2 md:mb-0 flex relative"
      ref={dropdownRef}
    >
      <span className="mr-2">{t("region.label")}</span>
      <span
        ref={triggerRef}
        className="flex items-center cursor-pointer"
        onClick={handleToggle}
      >
        <span className="mr-2 whitespace-nowrap">{siteData?.region?.name}</span>
        <span className="w-7 h-7 flex items-center justify-center">
          <img
            src={BeakRightSmallIcon}
            alt=""
            className="w-3 h-3 inline transition-transform duration-200"
            style={{ transform: isOpen ? "rotate(180deg)" : "rotate(0deg)" }}
          />
        </span>
      </span>

      {isOpen && otherRegions.length > 0 && (
        <div
          className="absolute bg-dark -top-2 z-50 min-w-[200px] bg-transparent border-0 p-0 m-0"
          style={{ left: `${modalLeftPosition}px` }}
        >
          {otherRegions.map((region) => (
            <div
              key={region.code}
              className="px-4 py-2 hover:bg-white/20 cursor-pointer transition-colors whitespace-nowrap"
              onClick={() => handleRegionSelect(region)}
            >
              {region.name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default RegionSwitch;
